import React, { useState, ChangeEvent } from 'react';
import { X } from 'lucide-react';
import { Modal } from 'common/components/Modal';
import { Button, Select } from 'common/components';
import { uploadContacts } from 'services/api/ContactServiceAPI';
import { UploadContactsResponse } from 'types/contact.type';
import { UploadContactsModalProps } from './UploadContactsModal.types';
import styles from './UploadContactsModal.module.scss';

const UploadContactsModal: React.FC<UploadContactsModalProps> = ({
    isOpen,
    onClose,
    workflows,
    onUploadSuccess,
    onUploadError,
}) => {
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>(
        workflows.length > 0 ? workflows[0].workflowId : ''
    );

    // Response modal state
    const [showResponseModal, setShowResponseModal] = useState<boolean>(false);
    const [invalidPhones, setInvalidPhones] = useState<string[]>([]);
    const [duplicatePhones, setDuplicatePhones] = useState<string[]>([]);

    // Handle file selection
    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setUploadFile(e.target.files[0]);
        }
    };

    // Clear the selected file
    const clearFileSelection = () => {
        setUploadFile(null);
        const fileInput = document.getElementById('csv-file-input') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    };

    // Handle workflow selection
    const handleWorkflowChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setSelectedWorkflowId(e.target.value);
    };

    // Trigger file input click
    const triggerFileInput = () => {
        const input = document.getElementById('csv-file-input') as HTMLInputElement;
        if (input) {
            input.click();
        }
    };

    // Close the main upload modal
    const handleClose = () => {
        if (!isUploading) {
            setUploadFile(null);
            if (workflows.length > 0) {
                setSelectedWorkflowId(workflows[0].workflowId);
            }
            onClose();
        }
    };

    // Close the response modal
    const closeResponseModal = () => {
        setShowResponseModal(false);
        setInvalidPhones([]);
        setDuplicatePhones([]);
    };

    // Handle file upload
    const handleUploadFile = async () => {
        if (!uploadFile) {
            onUploadError('Please select a CSV file to upload.');
            return;
        }

        if (!selectedWorkflowId) {
            onUploadError('Please select a workflow.');
            return;
        }

        try {
            setIsUploading(true);
            const fileReader = new FileReader();

            fileReader.onload = async (e) => {
                const csvText = e.target?.result as string;
                const lines = csvText.split('\n');
                const headers = lines[0].split(',').map((header) => header.trim());

                const requiredHeaders = ['firstName', 'lastName', 'phoneNumber'];
                const missingHeaders = requiredHeaders.filter((header) => !headers.includes(header));

                if (missingHeaders.length) {
                    onUploadError(`CSV is missing required headers: ${missingHeaders.join(', ')}`);
                    setIsUploading(false);
                    return;
                }

                const contactsData: Array<{
                    firstName: string;
                    lastName: string;
                    phoneNumber: string;
                    crmId?: string;
                    crmStatus?: string;
                }> = [];

                for (let i = 1; i < lines.length; i++) {
                    if (!lines[i].trim()) continue;

                    const values = lines[i].split(',').map((value) => value.trim());
                    const contact: Record<string, string> = {};

                    const requiredFields = ['firstName', 'lastName', 'phoneNumber'];
                    const optionalFields = ['crmId', 'crmStatus'];
                    const allowedFields = [...requiredFields, ...optionalFields];

                    headers.forEach((header, index) => {
                        if (allowedFields.includes(header)) {
                            contact[header] = values[index] || '';
                        }
                    });

                    if (!contact.firstName || !contact.lastName || !contact.phoneNumber) {
                        continue;
                    }

                    contactsData.push(
                        contact as {
                            firstName: string;
                            lastName: string;
                            phoneNumber: string;
                            crmId?: string;
                            crmStatus?: string;
                        }
                    );
                }

                try {
                    const response: UploadContactsResponse = await uploadContacts({
                        contacts: contactsData,
                        workflowId: selectedWorkflowId,
                    });

                    const hasInvalidPhones = response && response.invalidPhones && response.invalidPhones.length > 0;
                    const hasDuplicatePhones =
                        response && response.duplicatePhones && response.duplicatePhones.length > 0;

                    if (hasInvalidPhones || hasDuplicatePhones) {
                        setInvalidPhones(response.invalidPhones || []);
                        setDuplicatePhones(response.duplicatePhones || []);

                        handleClose();
                        setShowResponseModal(true);
                    } else {
                        handleClose();
                        onUploadSuccess();
                    }
                } catch (error) {
                    console.error('Error uploading file:', error);

                    const errorMsg =
                        (error as any).response?.data?.error ||
                        (error as Error).message ||
                        'Unknown error occurred while uploading contacts';

                    onUploadError(errorMsg);
                } finally {
                    setIsUploading(false);
                }
            };

            fileReader.readAsText(uploadFile);
        } catch (error) {
            console.error('Error reading file:', error);
            onUploadError('Error reading file: ' + ((error as Error).message || 'Unknown error'));
            setIsUploading(false);
        }
    };

    return (
        <>
            {/* Upload Modal for CSV Upload */}
            <Modal
                isOpen={isOpen}
                onClose={handleClose}
                title='Upload Contacts'
                size='md'
                showDefaultFooter={false}
                closeOnOverlayClick={!isUploading}
                closeOnEscape={!isUploading}
            >
                <div className={styles.modalBody}>
                    <p className={styles.modalText}>
                        Select a CSV file with columns: <strong>firstName, lastName, phoneNumber</strong> (required)
                    </p>
                    <p className={styles.modalText}>
                        Optional columns: <strong>crmId, crmStatus</strong> (only these optional fields are supported)
                    </p>
                    <div className={styles.fileInputContainer}>
                        <input
                            type='file'
                            id='csv-file-input'
                            accept='.csv'
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                        <Button
                            variant='outline-primary'
                            onClick={triggerFileInput}
                            className={styles.fileButton}
                            type='button'
                        >
                            Choose CSV File
                        </Button>
                        <div className={styles.fileNameContainer}>
                            <span className={styles.fileName}>{uploadFile ? uploadFile.name : 'No file selected'}</span>
                            {uploadFile && (
                                <Button
                                    variant='ghost'
                                    size='sm'
                                    icon={X}
                                    iconOnly
                                    onClick={clearFileSelection}
                                    className={styles.clearFileIcon}
                                    type='button'
                                    aria-label='Clear file selection'
                                />
                            )}
                        </div>
                    </div>

                    <div className={styles.workflowSelector}>
                        <label htmlFor='workflow-select'>Assign to Workflow:</label>
                        <Select
                            value={selectedWorkflowId}
                            onChange={handleWorkflowChange}
                            options={workflows.map((workflow) => ({
                                value: workflow.workflowId,
                                label: workflow.workflowName,
                            }))}
                        />
                    </div>

                    <div className={styles.modalFooter}>
                        <Button variant='outline-secondary' onClick={handleClose} disabled={isUploading}>
                            Cancel
                        </Button>
                        <Button variant='primary' onClick={handleUploadFile} disabled={isUploading}>
                            {isUploading ? 'Uploading...' : 'Upload'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Upload Response Modal for Invalid and Duplicate Phones */}
            <Modal
                isOpen={showResponseModal}
                onClose={closeResponseModal}
                title='Upload Results'
                size='md'
                showDefaultFooter={false}
            >
                <div className={styles.modalBody}>
                    {invalidPhones.length > 0 || duplicatePhones.length > 0 ? (
                        <div>
                            {invalidPhones.length > 0 && (
                                <>
                                    <p className={styles.modalText}>
                                        The following phone numbers are <strong>invalid</strong> and were not uploaded:
                                    </p>
                                    <ul className={styles.invalidPhonesList}>
                                        {invalidPhones.map((phone, index) => (
                                            <li key={`invalid-${index}`} className={styles.invalidPhone}>
                                                {phone}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className={styles.phoneFormatGuidance}>
                                        <p>Make sure your phone numbers follow the proper format:</p>
                                        <ul>
                                            <li>Include country code (e.g., +1 for US)</li>
                                            <li>Use only numbers, no spaces or special characters</li>
                                            <li>
                                                Example of valid format: <code>+15551234567</code>
                                            </li>
                                        </ul>
                                    </div>
                                </>
                            )}

                            {duplicatePhones.length > 0 && (
                                <>
                                    <p className={styles.modalText}>
                                        The following phone numbers are <strong>duplicates</strong> and were not
                                        uploaded:
                                    </p>
                                    <ul className={styles.invalidPhonesList}>
                                        {duplicatePhones.map((phone, index) => (
                                            <li key={`duplicate-${index}`} className={styles.duplicatePhone}>
                                                <span className={styles.duplicateLabel}>Duplicate</span> {phone}
                                            </li>
                                        ))}
                                    </ul>
                                    <p className={styles.modalText}>
                                        <small>
                                            These numbers already exist in the system and will not be added again.
                                        </small>
                                    </p>
                                </>
                            )}

                            <p className={styles.modalText}>
                                Please correct these phone numbers and try uploading them again.
                            </p>
                        </div>
                    ) : (
                        <p className={styles.modalText}>All contacts were uploaded successfully.</p>
                    )}

                    <div className={styles.modalFooter}>
                        <Button variant='outline-secondary' onClick={closeResponseModal}>
                            Close
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default UploadContactsModal;
